/**
 * Iter-157F.2 — Settings Calm-Down Pass: Scripts collapsible group.
 *
 * Wraps the Settings page's Group 02 (Scripts) and decides whether
 * to render its compact summary card or the full management
 * surface (script-setup + script-access sections).
 *
 * Decision matrix (server-driven via /scripts-summary):
 *
 *   - `is_healthy` = false  → ALWAYS expanded. The user is mid-
 *     onboarding (no script, build in progress, build failed, or
 *     review required) and we never hide onboarding actions.
 *
 *   - `is_healthy` = true   → defaults to COLLAPSED into a summary
 *     card. The user can opt-in to expand via the "Show Script
 *     Management" button; the preference is remembered in
 *     sessionStorage per-project so we don't keep snapping the
 *     surface shut while they're actively working.
 *
 * Pure presentation. No new behaviour. Script setup + Script access
 * children render exactly as before once expanded.
 */

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, FileText, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";

const SS_KEY_PREFIX = "osf:settings-scripts-expanded:";

function formatRelative(iso) {
    if (!iso) return null;
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const diff = Math.max(0, now - then);
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
        const days = Math.floor(hours / 24);
        if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
        const months = Math.floor(days / 30);
        if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
        const years = Math.floor(months / 12);
        return `${years} year${years === 1 ? "" : "s"} ago`;
    } catch {
        return null;
    }
}

export default function ScriptsCollapsibleGroup({ projectId, children }) {
    const apiBase = useApiBase();
    const [summary, setSummary] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const ssKey = useMemo(
        () => `${SS_KEY_PREFIX}${projectId || "_"}`,
        [projectId],
    );
    // Session-scoped expansion override. `null` = "follow server
    // health"; "1" = "user opted in to expand"; "0" = "user
    // explicitly collapsed". The override survives across reloads
    // within the same tab session but doesn't bleed between tab
    // closes (resetting on re-login is intentional).
    const [override, setOverride] = useState(() => {
        try {
            return sessionStorage.getItem(ssKey);
        } catch {
            return null;
        }
    });

    useEffect(() => {
        let cancelled = false;
        // Tiny fetch — defers display state until we know whether
        // the production is healthy or still being configured. If
        // the call fails (perms, network), we fail OPEN: render
        // the full surface so the user is never trapped behind a
        // collapsed UI they can't interact with.
        api.get(`${apiBase}/${projectId}/scripts-summary`)
            .then(({ data }) => {
                if (!cancelled) {
                    setSummary(data || null);
                    setLoaded(true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSummary(null);
                    setLoaded(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [projectId, apiBase]);

    // Defer rendering anything until the summary resolves — avoids
    // a flash of full-management UI for healthy projects.
    if (!loaded) {
        return (
            <div
                data-testid="settings-scripts-loading"
                className="rounded-xl border border-white/5 bg-card/40 p-5 mb-5 text-xs text-neutral-500"
            >
                Checking script status…
            </div>
        );
    }

    const isHealthy = Boolean(summary?.is_healthy);
    // Effective expanded:
    //   - if not healthy → always true (onboarding surface always visible)
    //   - if healthy and override === "1" → true
    //   - if healthy and override === "0" → false
    //   - if healthy and no override → false (default collapsed)
    let expanded;
    if (!isHealthy) {
        expanded = true;
    } else if (override === "1") {
        expanded = true;
    } else {
        expanded = false;
    }

    const persist = (next) => {
        try {
            sessionStorage.setItem(ssKey, next ? "1" : "0");
        } catch { /* private mode etc. */ }
        setOverride(next ? "1" : "0");
    };

    // Iter-157F.3 — pick a single attention pill for the unhealthy
    // case. "Silence is success" — we ONLY surface a badge when the
    // user actually needs to do something, and we never label the
    // happy path "Healthy" (that's internal/system language and
    // creates unnecessary questions about what "healthy" means).
    //
    // The order is deliberate: failed > review > building. If a
    // slot has both failed AND a pending review, the failure is
    // the actionable surface.
    let attention = null;
    const health = summary?.health || {};
    if (health.any_failed) {
        attention = { label: "Build failed", tone: "danger" };
    } else if (health.review_required) {
        attention = { label: "Review required", tone: "warning" };
    } else if (health.any_building) {
        attention = { label: "Build in progress", tone: "info" };
    }

    // Collapsed summary card — the calm everyday state.
    if (!expanded) {
        const episodes = summary?.episodes_count ?? 0;
        const scenes = summary?.scenes_count ?? 0;
        const cast = summary?.characters_count ?? 0;
        const relative = formatRelative(summary?.last_updated_at);
        const isEpisodic =
            summary?.production_type === "tv_series" || episodes > 1;
        return (
            <section
                data-testid="settings-scripts-summary"
                className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-primary/40"
            >
                <div className="flex items-center gap-2 mb-1">
                    <FileText size={14} className="text-primary" />
                    <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                        Scripts
                    </h2>
                    {/* Iter-157F.3 — Silence is success. No
                        "Healthy" badge in the collapsed state;
                        the summary itself is sufficient. We only
                        ever draw attention here when the user
                        needs to do something, which by
                        construction means the section is
                        EXPANDED, not collapsed. */}
                </div>
                <p className="text-[12px] text-neutral-400 mt-2 leading-relaxed">
                    The screenplay is uploaded, scenes and cast are built,
                    and no reviews are pending. Day-to-day work happens in
                    the{" "}
                    <strong className="text-white font-semibold">Script</strong>{" "}
                    tile on the project home; this section is now maintenance
                    rather than daily workflow.
                </p>
                <ul
                    data-testid="settings-scripts-summary-stats"
                    className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2"
                >
                    {isEpisodic ? (
                        <li
                            data-testid="settings-scripts-summary-episodes"
                            className="rounded-md border border-white/5 bg-secondary/30 px-3 py-2 flex items-center justify-between"
                        >
                            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                Episodes
                            </span>
                            <span className="text-sm font-semibold text-white">
                                {episodes}
                            </span>
                        </li>
                    ) : null}
                    <li
                        data-testid="settings-scripts-summary-scenes"
                        className="rounded-md border border-white/5 bg-secondary/30 px-3 py-2 flex items-center justify-between"
                    >
                        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Scenes
                        </span>
                        <span className="text-sm font-semibold text-white">
                            {scenes}
                        </span>
                    </li>
                    <li
                        data-testid="settings-scripts-summary-cast"
                        className="rounded-md border border-white/5 bg-secondary/30 px-3 py-2 flex items-center justify-between"
                    >
                        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Cast members
                        </span>
                        <span className="text-sm font-semibold text-white">
                            {cast}
                        </span>
                    </li>
                </ul>
                {relative ? (
                    <p
                        data-testid="settings-scripts-summary-last-updated"
                        className="text-[11px] text-neutral-500 mt-3 italic"
                    >
                        Last updated {relative}.
                    </p>
                ) : null}
                <button
                    type="button"
                    data-testid="settings-scripts-expand"
                    onClick={() => persist(true)}
                    className="instant-tap mt-4 inline-flex items-center gap-2 rounded-md bg-secondary border border-white/10 text-foreground px-3 h-9 text-xs font-semibold hover:bg-secondary/80"
                >
                    <ChevronDown size={14} />
                    Show Script Management
                </button>
            </section>
        );
    }

    // Expanded — render the children (script-setup + script-access)
    // exactly as before, prefaced by a tiny "collapse" affordance
    // when the production is healthy (we never offer Collapse on an
    // unhealthy state because onboarding must remain visible).
    // Iter-157F.3 — when unhealthy, also surface a single
    // attention pill describing WHY the section is open ("Build
    // failed" / "Review required" / "Build in progress"). When the
    // unhealthy state is simply "no script yet", we render no
    // pill — silence is success there too, the upload CTA is its
    // own affordance.
    const pillCls =
        attention?.tone === "danger"
            ? "text-red-200 bg-red-500/10 border-red-500/30"
            : attention?.tone === "warning"
              ? "text-amber-200 bg-amber-500/10 border-amber-500/30"
              : "text-sky-200 bg-sky-500/10 border-sky-500/30";
    const PillIcon =
        attention?.tone === "info" ? Loader2 : AlertTriangle;
    return (
        <div data-testid="settings-scripts-expanded">
            {attention ? (
                <div
                    data-testid="settings-scripts-attention"
                    data-attention-kind={attention.tone}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 mb-3 text-[11px] ${pillCls}`}
                >
                    <PillIcon
                        size={12}
                        className={
                            attention.tone === "info" ? "animate-spin" : ""
                        }
                    />
                    <span
                        data-testid="settings-scripts-attention-label"
                        className="font-semibold uppercase tracking-[0.2em] text-[10px]"
                    >
                        {attention.label}
                    </span>
                    <span className="opacity-80">
                        {attention.tone === "danger"
                            ? "— a script slot needs your attention below."
                            : attention.tone === "warning"
                              ? "— review the revision diff below before continuing."
                              : "— scenes and cast are being extracted; you can leave this page."}
                    </span>
                </div>
            ) : null}
            {isHealthy ? (
                <div className="flex items-center justify-end mb-2">
                    <button
                        type="button"
                        data-testid="settings-scripts-collapse"
                        onClick={() => persist(false)}
                        className="instant-tap inline-flex items-center gap-2 rounded-md bg-secondary/40 border border-white/5 text-neutral-300 px-3 h-8 text-[11px] font-semibold hover:bg-secondary/60"
                    >
                        <ChevronUp size={12} />
                        Hide Script Management
                    </button>
                </div>
            ) : null}
            {children}
        </div>
    );
}
